import React from 'react';
import { MoCAAssessment as MoCAAssessmentType, MoCADomainScore } from '../types';
import { calculateMoCAScore, getMoCAInterpretation } from '../utils/mocaScoring';
import './MoCAAssessment.css';

interface MoCAAssessmentProps {
  patientName: string;
  results: any[];
  onClose: () => void;
}

const MoCAAssessment: React.FC<MoCAAssessmentProps> = ({ patientName, results, onClose }) => {
  const mocaScore = calculateMoCAScore(results);
  const interpretation = getMoCAInterpretation(mocaScore.totalScore);

  const getScoreColor = (score: number): string => {
    if (score >= 26) return '#10B981'; // Green - Normal
    if (score >= 22) return '#F59E0B'; // Orange - MCI
    if (score >= 18) return '#EF4444'; // Red - Moderate
    return '#DC2626'; // Dark Red - Severe
  };

  const getDomainColor = (percentage: number): string => {
    if (percentage >= 80) return '#10B981';
    if (percentage >= 60) return '#F59E0B';
    if (percentage >= 40) return '#EF4444';
    return '#DC2626';
  };

  const formatDate = (isoString: string): string => {
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  return (
    <div className="moca-modal-overlay" onClick={onClose}>
      <div className="moca-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="moca-modal-header">
          <h2>MoCA Assessment Results</h2>
          <button className="moca-close-button" onClick={onClose}>×</button>
        </div>

        <div className="moca-patient-info">
          <h3>{patientName}</h3>
          <p className="moca-timestamp">Assessment Date: {formatDate(mocaScore.timestamp)}</p>
          <p className="moca-games-analyzed">Based on {mocaScore.gamesAnalyzed} cognitive games</p>
        </div>

        <div className="moca-total-score-section">
          <div className="moca-score-circle" style={{ borderColor: getScoreColor(mocaScore.totalScore) }}>
            <div className="moca-score-value" style={{ color: getScoreColor(mocaScore.totalScore) }}>
              {mocaScore.totalScore}
            </div>
            <div className="moca-score-max">/ {mocaScore.maxScore}</div>
          </div>
          <div className="moca-interpretation">
            <div className="moca-interpretation-level" style={{ color: getScoreColor(mocaScore.totalScore) }}>
              {interpretation.level}
            </div>
            <div className="moca-interpretation-description">
              {interpretation.description}
            </div>
          </div>
        </div>

        <div className="moca-domains-section">
          <h3>Cognitive Domains Breakdown</h3>
          <div className="moca-domains-grid">
            {mocaScore.domains.map((domain: MoCADomainScore, index: number) => (
              <div key={index} className="moca-domain-card">
                <div className="moca-domain-header">
                  <span className="moca-domain-name">{domain.domain}</span>
                  <span className="moca-domain-score" style={{ color: getDomainColor(domain.percentage) }}>
                    {domain.score.toFixed(1)} / {domain.maxScore}
                  </span>
                </div>
                <div className="moca-domain-bar-container">
                  <div 
                    className="moca-domain-bar-fill"
                    style={{ 
                      width: `${domain.percentage}%`,
                      backgroundColor: getDomainColor(domain.percentage)
                    }}
                  ></div>
                </div>
                <div className="moca-domain-percentage">
                  {domain.percentage.toFixed(0)}%
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="moca-recommendations-section">
          <h3>Clinical Recommendations</h3>
          <ul className="moca-recommendations-list">
            {interpretation.recommendations.map((rec: string, index: number) => (
              <li key={index}>{rec}</li>
            ))}
          </ul>
        </div>

        <div className="moca-disclaimer">
          <p><strong>Note:</strong> This assessment is based on cognitive game performance mapped to MoCA domains. 
          It is intended for screening purposes only and should not replace professional clinical evaluation. 
          MoCA cut-off: &lt;26 suggests possible cognitive impairment.</p>
        </div>

        <div className="moca-modal-footer">
          <button className="moca-close-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default MoCAAssessment;
